import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { NotificationContext, type NotificationSeverity } from './NotificationContext';

interface Notification {
  id: string;
  message: string;
  severity: NotificationSeverity;
}

interface Props {
  children: ReactNode;
}

export function NotificationProvider({ children }: Props) {
  const [queue, setQueue] = useState<Notification[]>([]);
  const [current, setCurrent] = useState<Notification | null>(null);
  const [open, setOpen] = useState(false);

  const enqueue = (message: string, severity: NotificationSeverity) => {
    setQueue((prev) => [...prev, { id: crypto.randomUUID(), message, severity }]);
  };

  const notifySuccess = (message: string) => enqueue(message, 'success');
  const notifyError = (message: string) => enqueue(message, 'error');

  // Consecutive-snackbars pattern: only ever show one at a time. When a new
  // notification arrives while one is visible, close the current one first —
  // the next is picked up from the queue once its exit transition finishes.
  useEffect(() => {
    if (!queue.length) return;
    if (!current) {
      setCurrent(queue[0]);
      setQueue((prev) => prev.slice(1));
      setOpen(true);
    } else if (open) {
      setOpen(false);
    }
  }, [queue, current, open]);

  const handleClose = (_event: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  const handleExited = () => setCurrent(null);

  return (
    <NotificationContext.Provider value={{ notifySuccess, notifyError }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        slotProps={{ transition: { onExited: handleExited } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={current?.severity}
          variant="filled"
          sx={{ width: '100%' }}
          data-testid="notification-snackbar"
        >
          {current?.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}
